import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Image as ImageIcon, FileDown, Loader2, X } from "lucide-react";

const MAX_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Dialog pour gérer les pièces jointes d'un SAV + générer la fiche PDF.
 */
export default function SAVAttachmentsDialog({ open, onClose, sav }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [previewAtt, setPreviewAtt] = useState(null);

  const fetchAtt = async () => {
    if (!sav?.id) return;
    setLoading(true);
    try {
      const res = await api.listSAVAttachments(sav.id);
      setAttachments(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) fetchAtt(); }, [open, sav?.id]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`${f.name} dépasse 8 Mo`);
          continue;
        }
        await api.uploadSAVAttachment(sav.id, f);
      }
      toast.success("Fichier(s) ajouté(s)");
      await fetchAtt();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'upload"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attId) => {
    try {
      await api.deleteSAVAttachment(sav.id, attId);
      await fetchAtt();
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const handleDownloadAttachment = (att) => {
    try {
      const link = document.createElement("a");
      link.href = `data:${att.mime_type};base64,${att.data_base64}`;
      link.download = att.filename;
      link.click();
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const handleGeneratePdf = async () => {
    setGenerating(true);
    try {
      const res = await api.getSAVWorksheetPdf(sav.id, includeAttachments);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiche_chantier_SAV_${(sav.client_name || "sav").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fiche PDF générée");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de génération PDF"));
    } finally {
      setGenerating(false);
    }
  };

  if (!sav) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sav-attachments-dialog">
          <DialogHeader>
            <DialogTitle>Photos & Documents — {sav.client_name}</DialogTitle>
          </DialogHeader>

          {/* Upload zone */}
          <label
            className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-accent/50 transition-colors"
            data-testid="sav-upload-zone"
          >
            <input
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
            />
            {uploading ? (
              <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
            ) : (
              <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
            )}
            <p className="mt-2 text-sm font-medium">Ajouter des photos ou documents</p>
            <p className="text-xs text-muted-foreground">PNG, JPG, PDF, DOCX, XLSX · Max 8 Mo par fichier</p>
          </label>

          {/* Existing attachments */}
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : attachments.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-4">Aucune pièce jointe pour ce SAV.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="sav-attachments-grid">
              {attachments.map((att) => (
                <div key={att.id} className="relative border rounded-md overflow-hidden bg-zinc-50 group">
                  {att.is_image ? (
                    <button
                      onClick={() => setPreviewAtt(att)}
                      className="block w-full"
                      data-testid={`preview-att-${att.id}`}
                    >
                      <img
                        src={`data:${att.mime_type};base64,${att.data_base64}`}
                        alt={att.filename}
                        className="w-full h-24 object-cover"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDownloadAttachment(att)}
                      className="flex flex-col items-center justify-center h-24 w-full text-muted-foreground hover:text-primary"
                    >
                      <FileText className="h-8 w-8" />
                      <span className="text-[10px] mt-1">Télécharger</span>
                    </button>
                  )}
                  <div className="p-1.5 bg-white border-t">
                    <p className="text-[10px] truncate" title={att.filename}>{att.filename}</p>
                    <p className="text-[9px] text-muted-foreground">{Math.round((att.size_bytes || 0) / 1024)} Ko</p>
                  </div>
                  <button
                    onClick={() => handleDelete(att.id)}
                    className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-white transition"
                    title="Supprimer"
                    data-testid={`delete-att-${att.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* PDF generation */}
          <div className="border-t pt-4 mt-2 space-y-3">
            <p className="text-sm font-semibold">Fiche de chantier PDF</p>
            <p className="text-xs text-muted-foreground">
              Génère une fiche destinée aux ouvriers avec coordonnées client, adresse, date, durée et descriptif.
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-att"
                checked={includeAttachments}
                onCheckedChange={(v) => setIncludeAttachments(!!v)}
                data-testid="pdf-include-attachments"
              />
              <Label htmlFor="include-att" className="text-xs cursor-pointer">
                Inclure les photos et documents en annexe ({attachments.length})
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            <Button onClick={handleGeneratePdf} disabled={generating} data-testid="generate-sav-pdf-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image preview */}
      {previewAtt && (
        <Dialog open={!!previewAtt} onOpenChange={(o) => !o && setPreviewAtt(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ImageIcon className="h-4 w-4" /> {previewAtt.filename}
              </DialogTitle>
            </DialogHeader>
            <img
              src={`data:${previewAtt.mime_type};base64,${previewAtt.data_base64}`}
              alt={previewAtt.filename}
              className="w-full h-auto max-h-[70vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
